import { useEffect, useState } from 'react'
import { createLogger } from '../../../app/logger'
import useAppDispatch from '../../../hooks/useAppDispatch'
import { setSseOpen } from '../sseSlice'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'ChronoSse'
})

const INJECTED_SSE_URL = process.env.REACT_APP_INJECTED_SSE_URL ?? 'http://localhost:4000'

type ChronoSseProps = {
  onMessage: (data: any) => void
  path: string
}

const ChronoSse = ({
  onMessage,
  path,
}: ChronoSseProps) => {
  const dispatch = useAppDispatch()
  const [eventSource, setEventSource] = useState<EventSource | null>(null)
  const [eventListener, setListener] = useState<globalThis.EventListener>()

  useEffect(() => {
    // cleanup fires on unmount and when effect changes,
    // this prevents accidentally closing eventsource
    // while still mounted
    let changed = true
    return () => {
      if (!changed) {
        eventSource?.removeEventListener('message', eventListener!)
        eventSource?.close()
      }
      changed = false
    }
  }, [eventListener, eventSource])

  useEffect(() => {
    setEventSource(oldEvtSrc => {
      let evtSrc = oldEvtSrc

      if (!oldEvtSrc?.url.endsWith(path)) {
        oldEvtSrc?.close()

        const url = `${INJECTED_SSE_URL}/${path}`
        logger.debug('opening event source for:', url)
        evtSrc = new EventSource(url, { withCredentials: true })
        evtSrc?.addEventListener('open', () => {
          logger.debug('SSE open')
          dispatch(setSseOpen(true))
        })
        evtSrc?.addEventListener('error', e => {
          logger.error('SSE error', e)
          dispatch(setSseOpen(false))
        })
        evtSrc?.addEventListener('close', e => {
          logger.debug('SSE closed', e)
          dispatch(setSseOpen(false))
        })
      }

      setListener((oldListener: any) => {
        evtSrc?.removeEventListener('message', oldListener)

        const listener = (msg: MessageEvent<any>): void => {
          try {
            logger.debug('Received SSE message: ', msg)

            const parsed = JSON.parse(msg.data)

            onMessage(parsed)
          } catch (err) {
            logger.error(err)
          }
        }
        evtSrc?.addEventListener('message', listener)

        return listener
      })

      return evtSrc
    })
  }, [onMessage, path, dispatch])

  return <></>
}

export default ChronoSse
